import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useGetCorrectLanguageCodeForPhoneInput, useValidationManager } from '@/utils/hooks';
import { ValidationForm, ValidatingInput, ValidationSummary } from '@/components/forms/validation';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Modal, PhoneInput, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { checkProfileInviteEmail, inviteProfileMember } from '@/services/profiles';
import { EmailValidity } from '@/types';
import { validateEmail } from '@/utils/string';

type Props = {
    isOpen: boolean;
    reload: () => void;

    toggle: () => void;
};

interface User {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
}
const InviteUserModal: React.FC<Props> = (props) => {
    const unitName = useSelector((state: IState) => state.buyer.projectData!.unitName);
    const [user, setUser] = useState<User>({ firstName: '', email: '', lastName: '', phoneNumber: '' });
    const inviterProfileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const projectId = useSelector((state: IState) => state.global.activeProject?.id);
    const currentLanguagePhoneLang = useGetCorrectLanguageCodeForPhoneInput();
    const validationManager = useValidationManager([{
        isValid: () => emailValidity !== EmailValidity.AlreadyExists,
        message: () => <Trans>A user with this email address already exists.</Trans>,
        summaryMessage: () => <Trans>A user with this email address already exists.</Trans>,
    }]);
    const [isSaving, setIsSaving] = useState(false);
    const [emailValidity, setEmailValid] = useState(EmailValidity.NotChecked);
    let currentCallId: any = null;

    const onEmailChange = ({ target: { value } }) => {
        const email = value.trim();
        setUser({ ...user, email });
        if (!validateEmail(email)) return;

        if (projectId === undefined) {
            console.error('ProjectID is undefined, cannot check if email already exists');
            return;
        }

        setEmailValid(EmailValidity.Checking);
        clearTimeout(currentCallId);
        setTimeout(() => {
            checkProfileInviteEmail({
                profileId: inviterProfileId,
                inviteeEmail: email,
            }).then((x) => {
                setEmailValid(x);
            }).catch(err => console.error(err));
        }, 500);
    };

    const onSave = async () => {
        setIsSaving(true);
        await inviteProfileMember({
            inviteeEmail: user.email,
            inviteeFirstName: user.firstName,
            inviteeLastName: user.lastName,
            inviteePhoneNumber: user.phoneNumber,
            inviterProfileId,
            profilePermissions: [],
            isAdministrator: false,
        });
        props.reload();
        setIsSaving(false);
        Toast.success(<Trans>Invitation sent</Trans>);
        props.toggle();
    };

    return (
        <Modal toggle={props.toggle} isOpen={props.isOpen}>
            <ValidationForm validationManager={validationManager} onSubmit={onSave} className="form">
                <h5>
                    <Trans>Invite a user for {unitName}</Trans>
                </h5>
                <ValidatingInput
                    className="form-control form-group"
                    placeholder={t`First name`}
                    onChange={(e) => setUser({ ...user, firstName: e.target.value })}
                    value={user.firstName}
                />
                <ValidatingInput
                    className="form-control form-group"
                    placeholder={t`Last name`}
                    onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                    value={user.lastName}
                />
                <div className="form-group">
                    <PhoneInput
                        className="form-control"
                        required
                        fieldName={t`Phone number`}
                        onChange={(v, c, isValid) => {
                            if (isValid) setUser({ ...user, phoneNumber: v });
                        }}
                        value={user.phoneNumber}
                        initialCountry={currentLanguagePhoneLang}
                    />
                </div>
                <ValidatingInput
                    className="form-control form-group"
                    placeholder={t`Email`}
                    onChange={onEmailChange}
                    value={user.email}
                />

                <ValidationSummary />
                <div className="buttons">
                    <Button disabled={isSaving} color="tertiary" onClick={props.toggle}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        disabled={emailValidity === EmailValidity.AlreadyExists}
                        type="submit"
                        loading={isSaving}
                        color="primary"
                    >
                        <Trans>Invite</Trans>
                    </Button>
                </div>
            </ValidationForm>
        </Modal>
    );
};

export default InviteUserModal;
